import { useEffect, useMemo, useState, useRef } from "react";
import { Center, Flex, Loader, useMantineTheme, Text } from "@mantine/core";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  type MRT_SortingState,
  type MRT_ColumnFiltersState,
} from "mantine-react-table";
import { useDebouncedValue } from "@mantine/hooks";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";

interface AdminCreditHistoryTabProps {
  companyId: string;
}

const DIRECTION_LABELS: Record<string, string> = {
  CREDIT: "Credit",
  DEBIT: "Debit",
};

const REASON_LABELS: Record<string, string> = {
  TOPUP: "Top Up",
  SIGNUP_BONUS: "Signup Bonus",
  VERIFICATION_FEE: "Verification Fee",
  ENGAGEMENT_FEE: "Engagement Fee",
  REFUND_ENGAGEMENT: "Refund",
};

const SOURCE_LABELS: Record<string, string> = {
  RAZORPAY: "Razorpay",
  WALLET: "Wallet",
  SYSTEM: "System",
};

type CreditHistoryItem = {
  id: string;
  direction: string;
  source: string;
  reason: string;
  amount: number;
  performedBy: string;
  createdAt: Date;
};

const PAGE_SIZE = 10;

export default function AdminCreditHistoryTab({ companyId }: AdminCreditHistoryTabProps) {
  const theme = useMantineTheme();

  const [creditHistory, setCreditHistory] = useState<CreditHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [debouncedFilters] = useDebouncedValue(columnFilters, 400);

  const previousParamsRef = useRef<string>("");

  const filterParams = useMemo(() => {
    const filtered = debouncedFilters.filter((f) => f.value !== undefined && f.value !== null && f.value !== "");
    return Object.fromEntries(filtered.map((f) => [f.id, f.value]));
  }, [debouncedFilters]);

  useEffect(() => {
    const params = {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      sorting: sorting.map((s) => ({ id: s.id, desc: s.desc })),
    };

    const paramsString = JSON.stringify({ ...params, filterParams });

    if (paramsString === previousParamsRef.current) {
      return;
    }

    previousParamsRef.current = paramsString;

    const fetchCreditHistory = async () => {
      setLoading(true);
      try {
        const filter: Record<string, any> = {};
        if (filterParams.direction) filter.direction = filterParams.direction;
        if (filterParams.source) filter.source = filterParams.source;
        if (filterParams.reason) filter.reason = filterParams.reason;

        const res = await trpc.admin.getCreditHistory.query({
          companyId,
          page: params.page,
          limit: params.limit,
          filter: Object.keys(filter).length > 0 ? filter : undefined,
          sorting: params.sorting.length > 0 ? params.sorting : undefined,
        });

        setCreditHistory(
          res.creditHistory.map((entry: any) => ({
            id: entry.id,
            direction: entry.direction,
            source: entry.source,
            reason: entry.reason,
            amount: entry.amount,
            performedBy: entry.performedBy,
            createdAt: new Date(entry.createdAt),
          }))
        );
        setTotalRows(res.meta.total);
      } catch (err) {
        handleTrpcError(err);
        setCreditHistory([]);
        setTotalRows(0);
      } finally {
        setLoading(false);
      }
    };

    fetchCreditHistory();
  }, [companyId, pagination.pageIndex, pagination.pageSize, filterParams, sorting]);

  useEffect(() => {
    if (pagination.pageIndex !== 0) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [filterParams]);

  const columns: MRT_ColumnDef<CreditHistoryItem>[] = useMemo(
    () => [
      {
        accessorKey: "performedBy",
        header: "Performed By",
        size: 180,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const value = cell.getValue() as string;
          return (
            <Text size="sm" c="slate.7">
              {value || "—"}
            </Text>
          );
        },
      },
      {
        accessorKey: "reason",
        header: "Reason",
        size: 160,
        filterVariant: "select",
        mantineFilterSelectProps: {
          data: Object.entries(REASON_LABELS).map(([value, label]) => ({
            value,
            label,
          })),
          clearable: true,
        },
        Cell: ({ cell }) => {
          const value = cell.getValue() as string;
          return (
            <Text size="sm" c="slate.7">
              {REASON_LABELS[value] || value}
            </Text>
          );
        },
      },
      {
        accessorKey: "direction",
        header: "Type",
        size: 100,
        filterVariant: "select",
        mantineFilterSelectProps: {
          data: Object.entries(DIRECTION_LABELS).map(([value, label]) => ({
            value,
            label,
          })),
          clearable: true,
        },
        Cell: ({ cell }) => {
          const value = cell.getValue() as string;
          const isCredit = value === "CREDIT";
          return (
            <Text size="sm" fw={500} c={isCredit ? "#16a34a" : "#dc2626"}>
              {DIRECTION_LABELS[value] || value}
            </Text>
          );
        },
      },
      {
        accessorKey: "amount",
        header: "Amount",
        size: 100,
        enableColumnFilter: false,
        Cell: ({ cell, row }) => {
          const amount = cell.getValue() as number;
          const isCredit = row.original.direction === "CREDIT";
          return (
            <Text size="sm" fw={600} c={isCredit ? "#16a34a" : "#dc2626"}>
              {isCredit ? "+" : "-"}
              {amount}
            </Text>
          );
        },
      },
      {
        accessorKey: "source",
        header: "Source",
        size: 120,
        filterVariant: "select",
        mantineFilterSelectProps: {
          data: Object.entries(SOURCE_LABELS).map(([value, label]) => ({
            value,
            label,
          })),
          clearable: true,
        },
        Cell: ({ cell }) => {
          const value = cell.getValue() as string;
          return (
            <Text size="sm" c="slate.7">
              {SOURCE_LABELS[value] || value}
            </Text>
          );
        },
      },
      {
        accessorKey: "createdAt",
        header: "Date & Time",
        size: 200,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const date = cell.getValue() as Date;
          return (
            <Text size="sm" c="slate.6">
              {date.toLocaleString("en-IN", {
                dateStyle: "medium",
                timeStyle: "short",
              })}
            </Text>
          );
        },
      },
    ],
    []
  );

  if (loading && creditHistory.length === 0) {
    return (
      <Center h="300px">
        <Loader size="lg" variant="dots" />
      </Center>
    );
  }

  return (
    <Flex direction="column" w="100%">
      <MantineReactTable
        columns={columns}
        data={creditHistory}
        enableRowNumbers
        enableStickyHeader
        enableColumnPinning
        enableColumnResizing
        columnResizeMode="onEnd"
        layoutMode="semantic"
        manualPagination
        manualFiltering
        manualSorting
        enableGlobalFilter={false}
        rowCount={totalRows}
        onPaginationChange={setPagination}
        onColumnFiltersChange={setColumnFilters}
        onSortingChange={setSorting}
        state={{
          pagination,
          columnFilters,
          sorting,
          isLoading: loading,
        }}
        initialState={{
          density: "md",
        }}
        mantineTableBodyCellProps={{
          style: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: 200,
          },
        }}
        mantineTableHeadCellProps={{
          style: {
            fontWeight: 500,
            fontSize: "var(--mantine-font-size-md)",
            color: theme.colors.slate[6],
          },
        }}
        mantineTableContainerProps={{
          style: {
            maxHeight: "400px",
            minHeight: "100px",
            width: "100%",
            overflowX: "auto",
          },
        }}
        renderEmptyRowsFallback={() => (
          <tr>
            <td colSpan={columns.length + 1} style={{ textAlign: "center", padding: "2rem" }}>
              No credit history found for this company.
            </td>
          </tr>
        )}
      />
    </Flex>
  );
}
